// Sprint time-box helpers (TPT589): how a sprint's start_date/end_date read in the sprint board's
// lane header (pages/sprint-board.js) and Sprint Planning's date strip (pages/sprint-planning.js).
// Pure (no DOM, no api.js) so node:test can import it without stubs — same shape as
// lib/due-tasks.js, whose calendar-day rules it shares: `YYYY-MM-DD` strings (the sprints table's
// DATE columns) against the viewer's LOCAL calendar day.

import { todayIso, dueDayDiff, shortDay, formatDay } from './due-tasks.js';

// A sprint date as a usable `YYYY-MM-DD` string, or null (unset or malformed).
function day(value) {
  return typeof value === 'string' && shortDay(value) ? value : null;
}

// Use all project sprints, including lanes hidden by board filters.
export function manualSprintOverlaps(sprint, sprints) {
  if (sprint?.date_source !== 'manual' || !day(sprint.start_date) || !day(sprint.end_date)) return false;
  const previous = sprints.reduce((nearest, row) => (
    row.number > 0 && row.number < sprint.number && day(row.end_date)
      && (!nearest || row.number > nearest.number) ? row : nearest
  ), null);
  return !!previous && sprint.start_date <= previous.end_date
    && (!day(previous.start_date) || sprint.end_date >= previous.start_date);
}

export function sprintOverlapText(noun = 'Sprint', language = 'en') {
  if (language === 'uk') {
    const previous = { Sprint: 'попереднім спринтом', Batch: 'попереднім пакетом',
      Bag: 'попереднім мішком', Jar: 'попередньою банкою', Group: 'попередньою групою', Step: 'попереднім кроком' };
    return `Перетинається з ${previous[noun] || 'попередньою групою'}`;
  }
  return `Overlaps the previous ${noun.toLowerCase()}`;
}

// "Oct 6 – Oct 19". The year is added when the two dates fall in different years
// ("Dec 28, 2026 – Jan 8, 2027") or in a year other than today's ("Oct 6 – Oct 19, 2025").
// One date only: "From Oct 6" / "Until Oct 19". '' with neither.
export function sprintRangeLabel(start, end, today = todayIso()) {
  const s = day(start);
  const e = day(end);
  if (!s && !e) return '';
  const thisYear = String(today).slice(0, 4);
  const yearSuffix = (iso) => (iso.slice(0, 4) === thisYear ? '' : `, ${iso.slice(0, 4)}`);
  if (s && e) {
    if (s.slice(0, 4) !== e.slice(0, 4)) return `${formatDay(s)} – ${formatDay(e)}`;
    return `${shortDay(s)} – ${shortDay(e)}${yearSuffix(s)}`;
  }
  return s ? `From ${shortDay(s)}${yearSuffix(s)}` : `Until ${shortDay(e)}${yearSuffix(e)}`;
}

// Where a sprint stands against its dates — null when neither date is set (callers then render
// nothing at all). `phase`:
//   'upcoming' — today is before the start;
//   'active'   — the start (if any) has come and today is not past the end; `daysLeft` is the whole
//                days until the end, 0 on the last day;
//   'ended'    — today is past the end;
//   'open'     — started, with no end date: no countdown, empty `label`.
// `lengthDays` counts both ends (Oct 6 – Oct 19 is 14), null without both dates; `title` is the
// full range for a tooltip.
export function sprintSchedule(start, end, today = todayIso()) {
  const s = day(start);
  const e = day(end);
  if (!s && !e) return null;
  const range = sprintRangeLabel(s, e, today);
  const title = s && e ? `${formatDay(s)} – ${formatDay(e)}` : (s ? `Starts ${formatDay(s)}` : `Ends ${formatDay(e)}`);
  const span = s && e ? dueDayDiff(e, s) : null;
  const lengthDays = span != null && span >= 0 ? span + 1 : null;
  const untilStart = s ? dueDayDiff(s, today) : null;
  const untilEnd = e ? dueDayDiff(e, today) : null;

  let phase = 'open';
  let daysLeft = null;
  let label = '';
  if (untilStart != null && untilStart > 0) {
    phase = 'upcoming';
    label = untilStart === 1 ? 'Starts tomorrow' : `Starts in ${untilStart} days`;
  } else if (untilEnd != null && untilEnd < 0) {
    phase = 'ended';
    label = untilEnd === -1 ? 'Ended yesterday' : `Ended ${-untilEnd} days ago`;
  } else if (untilEnd != null) {
    phase = 'active';
    daysLeft = untilEnd;
    label = untilEnd === 0 ? 'Ends today' : (untilEnd === 1 ? '1 day left' : `${untilEnd} days left`);
  }
  return { range, title, phase, lengthDays, daysLeft, label };
}

// "14 days · 5 days left" — a schedule's length and countdown, whichever it has; '' for none.
export function scheduleSummary(schedule) {
  if (!schedule) return '';
  return [schedule.lengthDays ? `${schedule.lengthDays} days` : '', schedule.label].filter(Boolean).join(' · ');
}

// The inline message for a failed sprint-date save. The API answers a rejected pair with a 400
// `invalid_sprint_dates` (end before start, or not a real calendar day — a date input can only
// send the former); any other failure keeps its own message.
export function sprintDateErrorText(err) {
  const code = err && ((err.body && err.body.error) || err.message);
  if (code === 'invalid_sprint_dates') return "End date can't be before the start date.";
  return (err && err.message) || 'Failed to save dates';
}
